/* tslint:disable */
/* eslint-disable */
import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse, HttpContext } from '@angular/common/http';
import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';
import { Observable } from 'rxjs';
import { map, filter } from 'rxjs/operators';

import { GetMerchantSitesResponse } from '../models/get-merchant-sites-response';
import { InputStreamSource } from '../models/input-stream-source';
import { ListMarketingDataViewModel } from '../models/list-marketing-data-view-model';
import { MarketingDataDto } from '../models/marketing-data-dto';
import { MarketingDataViewModel } from '../models/marketing-data-view-model';
import { MerchantBasicDataViewModel } from '../models/merchant-basic-data-view-model';
import { MerchantContactsViewModel } from '../models/merchant-contacts-view-model';
import { MerchantResponse } from '../models/merchant-response';
import { MerchantSearchDetailsFilter } from '../models/merchant-search-details-filter';
import { MerchantSiteMerchantTypeViewModel } from '../models/merchant-site-merchant-type-view-model';
import { MerchantSiteViewModel } from '../models/merchant-site-view-model';
import { MerchantViewModel } from '../models/merchant-view-model';
import { PageMerchantSearchResponse } from '../models/page-merchant-search-response';
import { PageSiteMerchantTypesResponse } from '../models/page-site-merchant-types-response';
import { PagedModelDeliveryMethodPageResponse } from '../models/paged-model-delivery-method-page-response';
import { PagedModelMerchantResponse } from '../models/paged-model-merchant-response';
import { PagedModelTenderResponseModel } from '../models/paged-model-tender-response-model';
import { PostMerchantRequest } from '../models/post-merchant-request';
import { PostMerchantSiteRequest } from '../models/post-merchant-site-request';
import { PutMerchantSiteRequest } from '../models/put-merchant-site-request';
import { ServedCustomerSegmentResponse } from '../models/served-customer-segment-response';
import { SiteMerchantTypeStatusUpdateRequest } from '../models/site-merchant-type-status-update-request';
import { SiteResponseModel } from '../models/site-response-model';
import { SiteSelectItemDto } from '../models/site-select-item-dto';

/**
 * Merchants endpoints.
 */
@Injectable({
  providedIn: 'root',
})
export class MerchantsService extends BaseService {
  constructor(config: ApiConfiguration, http: HttpClient) {
    super(config, http);
  }

  /**
   * Path part for operation getMerchantSite
   */
  static readonly GetMerchantSitePath =
    '/merchants/{merchantId}/sites/{siteId}';

  /**
   * Create a new site of merchant.
   *
   * Finds and returns the saved resource with the specified id.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getMerchantSite()` instead.
   *
   * This method doesn't expect any request body.
   */
  getMerchantSite$Response(
    params: {
      merchantId: string;
      siteId: string;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<SiteResponseModel>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      MerchantsService.GetMerchantSitePath,
      'get'
    );
    if (params) {
      rb.path('merchantId', params.merchantId, {});
      rb.path('siteId', params.siteId, {});
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/hal+json',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<SiteResponseModel>;
        })
      );
  }

  /**
   * Create a new site of merchant.
   *
   * Finds and returns the saved resource with the specified id.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getMerchantSite$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getMerchantSite(
    params: {
      merchantId: string;
      siteId: string;
    },
    context?: HttpContext
  ): Observable<SiteResponseModel> {
    return this.getMerchantSite$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<SiteResponseModel>) =>
          r.body as SiteResponseModel
      )
    );
  }

  /**
   * Path part for operation updateSite
   */
  static readonly UpdateSitePath = '/merchants/{merchantId}/sites/{siteId}';

  /**
   * Updates contants of merchant.
   *
   * Finds and returns the saved resource with the specified id.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateSite()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateSite$Response(
    params: {
      merchantId: string;
      siteId: string;
      body: PutMerchantSiteRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<void>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      MerchantsService.UpdateSitePath,
      'put'
    );
    if (params) {
      rb.path('merchantId', params.merchantId, {});
      rb.path('siteId', params.siteId, {});
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({
          responseType: 'text',
          accept: '*/*',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return (r as HttpResponse<any>).clone({
            body: undefined,
          }) as StrictHttpResponse<void>;
        })
      );
  }

  /**
   * Updates contants of merchant.
   *
   * Finds and returns the saved resource with the specified id.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updateSite$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateSite(
    params: {
      merchantId: string;
      siteId: string;
      body: PutMerchantSiteRequest;
    },
    context?: HttpContext
  ): Observable<void> {
    return this.updateSite$Response(params, context).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation deleteSite
   */
  static readonly DeleteSitePath = '/merchants/{merchantId}/sites/{siteId}';

  /**
   * Updates contants of merchant.
   *
   * Finds and returns the saved resource with the specified id.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `deleteSite()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteSite$Response(
    params: {
      merchantId: string;
      siteId: string;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<void>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      MerchantsService.DeleteSitePath,
      'delete'
    );
    if (params) {
      rb.path('merchantId', params.merchantId, {});
      rb.path('siteId', params.siteId, {});
    }

    return this.http
      .request(
        rb.build({
          responseType: 'text',
          accept: '*/*',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return (r as HttpResponse<any>).clone({
            body: undefined,
          }) as StrictHttpResponse<void>;
        })
      );
  }

  /**
   * Updates contants of merchant.
   *
   * Finds and returns the saved resource with the specified id.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `deleteSite$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteSite(
    params: {
      merchantId: string;
      siteId: string;
    },
    context?: HttpContext
  ): Observable<void> {
    return this.deleteSite$Response(params, context).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation getOperatingLicense
   */
  static readonly GetOperatingLicensePath =
    '/merchants/{merchantId}/sites/{siteId}/operating-license';

  /**
   * Gets operating license of the given site.
   *
   * Only merchants can see their license, or merchants who have incoming purchases from the owner of the license.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getOperatingLicense()` instead.
   *
   * This method doesn't expect any request body.
   */
  getOperatingLicense$Response(
    params: {
      merchantId: string;
      siteId: string;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<InputStreamSource>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      MerchantsService.GetOperatingLicensePath,
      'get'
    );
    if (params) {
      rb.path('merchantId', params.merchantId, {});
      rb.path('siteId', params.siteId, {});
    }

    return this.http
      .request(
        rb.build({
          responseType: 'blob',
          accept: 'application/octet-stream',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<InputStreamSource>;
        })
      );
  }

  /**
   * Gets operating license of the given site.
   *
   * Only merchants can see their license, or merchants who have incoming purchases from the owner of the license.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getOperatingLicense$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getOperatingLicense(
    params: {
      merchantId: string;
      siteId: string;
    },
    context?: HttpContext
  ): Observable<InputStreamSource> {
    return this.getOperatingLicense$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<InputStreamSource>) =>
          r.body as InputStreamSource
      )
    );
  }

  /**
   * Path part for operation uploadOperatingLicense
   */
  static readonly UploadOperatingLicensePath =
    '/merchants/{merchantId}/sites/{siteId}/operating-license';

  /**
   * Updates operating license to the given site.
   *
   * Valid file formats to upload: .pdf, .jpeg, .bmp
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `uploadOperatingLicense()` instead.
   *
   * This method sends `multipart/form-data` and handles request body of type `multipart/form-data`.
   */
  uploadOperatingLicense$Response(
    params: {
      merchantId: string;
      siteId: string;
      body?: {
        file: Blob;
      };
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<void>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      MerchantsService.UploadOperatingLicensePath,
      'put'
    );
    if (params) {
      rb.path('merchantId', params.merchantId, {});
      rb.path('siteId', params.siteId, {});
      rb.body(params.body, 'multipart/form-data');
    }

    return this.http
      .request(
        rb.build({
          responseType: 'text',
          accept: '*/*',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return (r as HttpResponse<any>).clone({
            body: undefined,
          }) as StrictHttpResponse<void>;
        })
      );
  }

  /**
   * Updates operating license to the given site.
   *
   * Valid file formats to upload: .pdf, .jpeg, .bmp
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `uploadOperatingLicense$Response()` instead.
   *
   * This method sends `multipart/form-data` and handles request body of type `multipart/form-data`.
   */
  uploadOperatingLicense(
    params: {
      merchantId: string;
      siteId: string;
      body?: {
        file: Blob;
      };
    },
    context?: HttpContext
  ): Observable<void> {
    return this.uploadOperatingLicense$Response(params, context).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation getMarketingDataByMerchant
   */
  static readonly GetMarketingDataByMerchantPath =
    '/merchants/{merchantId}/marketing-data/{marketingDataId}';

  /**
   * Get marketing data by id.
   *
   * Finds and returns the specified marketing data
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getMarketingDataByMerchant()` instead.
   *
   * This method doesn't expect any request body.
   */
  getMarketingDataByMerchant$Response(
    params: {
      merchantId: string;
      marketingDataId: string;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<MarketingDataViewModel>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      MerchantsService.GetMarketingDataByMerchantPath,
      'get'
    );
    if (params) {
      rb.path('merchantId', params.merchantId, {});
      rb.path('marketingDataId', params.marketingDataId, {});
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<MarketingDataViewModel>;
        })
      );
  }

  /**
   * Get marketing data by id.
   *
   * Finds and returns the specified marketing data
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getMarketingDataByMerchant$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getMarketingDataByMerchant(
    params: {
      merchantId: string;
      marketingDataId: string;
    },
    context?: HttpContext
  ): Observable<MarketingDataViewModel> {
    return this.getMarketingDataByMerchant$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<MarketingDataViewModel>) =>
          r.body as MarketingDataViewModel
      )
    );
  }

  /**
   * Path part for operation updateMarketingDataByMerchant
   */
  static readonly UpdateMarketingDataByMerchantPath =
    '/merchants/{merchantId}/marketing-data/{marketingDataId}';

  /**
   * Updates marketing data.
   *
   * Update the specified marketing data
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateMarketingDataByMerchant()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateMarketingDataByMerchant$Response(
    params: {
      merchantId: string;
      marketingDataId: string;
      body: MarketingDataDto;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<void>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      MerchantsService.UpdateMarketingDataByMerchantPath,
      'put'
    );
    if (params) {
      rb.path('merchantId', params.merchantId, {});
      rb.path('marketingDataId', params.marketingDataId, {});
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({
          responseType: 'text',
          accept: '*/*',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return (r as HttpResponse<any>).clone({
            body: undefined,
          }) as StrictHttpResponse<void>;
        })
      );
  }

  /**
   * Updates marketing data.
   *
   * Update the specified marketing data
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updateMarketingDataByMerchant$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateMarketingDataByMerchant(
    params: {
      merchantId: string;
      marketingDataId: string;
      body: MarketingDataDto;
    },
    context?: HttpContext
  ): Observable<void> {
    return this.updateMarketingDataByMerchant$Response(params, context).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation deleteMarketingDataByMerchant
   */
  static readonly DeleteMarketingDataByMerchantPath =
    '/merchants/{merchantId}/marketing-data/{marketingDataId}';

  /**
   * Delete marketing data.
   *
   * Delete specified marketing data entity
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `deleteMarketingDataByMerchant()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteMarketingDataByMerchant$Response(
    params: {
      merchantId: string;
      marketingDataId: string;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<void>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      MerchantsService.DeleteMarketingDataByMerchantPath,
      'delete'
    );
    if (params) {
      rb.path('merchantId', params.merchantId, {});
      rb.path('marketingDataId', params.marketingDataId, {});
    }

    return this.http
      .request(
        rb.build({
          responseType: 'text',
          accept: '*/*',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return (r as HttpResponse<any>).clone({
            body: undefined,
          }) as StrictHttpResponse<void>;
        })
      );
  }

  /**
   * Delete marketing data.
   *
   * Delete specified marketing data entity
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `deleteMarketingDataByMerchant$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteMarketingDataByMerchant(
    params: {
      merchantId: string;
      marketingDataId: string;
    },
    context?: HttpContext
  ): Observable<void> {
    return this.deleteMarketingDataByMerchant$Response(params, context).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation getMerchantLogo
   */
  static readonly GetMerchantLogoPath = '/merchants/{merchantId}/logo';

  /**
   * Gets the merchant logo.
   *
   * Finds and returns the saved resource with the specified id.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getMerchantLogo()` instead.
   *
   * This method doesn't expect any request body.
   */
  getMerchantLogo$Response(
    params: {
      merchantId: string;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<string>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      MerchantsService.GetMerchantLogoPath,
      'get'
    );
    if (params) {
      rb.path('merchantId', params.merchantId, {});
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<string>;
        })
      );
  }

  /**
   * Gets the merchant logo.
   *
   * Finds and returns the saved resource with the specified id.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getMerchantLogo$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getMerchantLogo(
    params: {
      merchantId: string;
    },
    context?: HttpContext
  ): Observable<string> {
    return this.getMerchantLogo$Response(params, context).pipe(
      map((r: StrictHttpResponse<string>) => r.body as string)
    );
  }

  /**
   * Path part for operation updateMerchantLogo
   */
  static readonly UpdateMerchantLogoPath = '/merchants/{merchantId}/logo';

  /**
   * Updates merchant logo.
   *
   * Finds and returns the saved resource with the specified id.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateMerchantLogo()` instead.
   *
   * This method sends `multipart/form-data` and handles request body of type `multipart/form-data`.
   */
  updateMerchantLogo$Response(
    params: {
      merchantId: string;
      body?: {
        file: Blob;
      };
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<void>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      MerchantsService.UpdateMerchantLogoPath,
      'put'
    );
    if (params) {
      rb.path('merchantId', params.merchantId, {});
      rb.body(params.body, 'multipart/form-data');
    }

    return this.http
      .request(
        rb.build({
          responseType: 'text',
          accept: '*/*',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return (r as HttpResponse<any>).clone({
            body: undefined,
          }) as StrictHttpResponse<void>;
        })
      );
  }

  /**
   * Updates merchant logo.
   *
   * Finds and returns the saved resource with the specified id.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updateMerchantLogo$Response()` instead.
   *
   * This method sends `multipart/form-data` and handles request body of type `multipart/form-data`.
   */
  updateMerchantLogo(
    params: {
      merchantId: string;
      body?: {
        file: Blob;
      };
    },
    context?: HttpContext
  ): Observable<void> {
    return this.updateMerchantLogo$Response(params, context).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation listMerchantContact
   */
  static readonly ListMerchantContactPath = '/merchants/{merchantId}/contacts';

  /**
   * Gets contacts of merchant.
   *
   * Finds and returns the saved resource with the specified id.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `listMerchantContact()` instead.
   *
   * This method doesn't expect any request body.
   */
  listMerchantContact$Response(
    params: {
      merchantId: string;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<MerchantContactsViewModel>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      MerchantsService.ListMerchantContactPath,
      'get'
    );
    if (params) {
      rb.path('merchantId', params.merchantId, {});
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<MerchantContactsViewModel>;
        })
      );
  }

  /**
   * Gets contacts of merchant.
   *
   * Finds and returns the saved resource with the specified id.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `listMerchantContact$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  listMerchantContact(
    params: {
      merchantId: string;
    },
    context?: HttpContext
  ): Observable<MerchantContactsViewModel> {
    return this.listMerchantContact$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<MerchantContactsViewModel>) =>
          r.body as MerchantContactsViewModel
      )
    );
  }

  /**
   * Path part for operation updateMerchantContact
   */
  static readonly UpdateMerchantContactPath =
    '/merchants/{merchantId}/contacts';

  /**
   * Updates contacts of merchant.
   *
   * Finds and returns the saved resource with the specified id.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateMerchantContact()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateMerchantContact$Response(
    params: {
      merchantId: string;
      body: MerchantContactsViewModel;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<void>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      MerchantsService.UpdateMerchantContactPath,
      'put'
    );
    if (params) {
      rb.path('merchantId', params.merchantId, {});
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({
          responseType: 'text',
          accept: '*/*',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return (r as HttpResponse<any>).clone({
            body: undefined,
          }) as StrictHttpResponse<void>;
        })
      );
  }

  /**
   * Updates contacts of merchant.
   *
   * Finds and returns the saved resource with the specified id.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updateMerchantContact$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateMerchantContact(
    params: {
      merchantId: string;
      body: MerchantContactsViewModel;
    },
    context?: HttpContext
  ): Observable<void> {
    return this.updateMerchantContact$Response(params, context).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation getMerchantBasicData
   */
  static readonly GetMerchantBasicDataPath =
    '/merchants/{merchantId}/basic-data';

  /**
   * Gets merchant.
   *
   * Finds and returns the saved resource with the specified id.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getMerchantBasicData()` instead.
   *
   * This method doesn't expect any request body.
   */
  getMerchantBasicData$Response(
    params: {
      merchantId: string;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<MerchantBasicDataViewModel>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      MerchantsService.GetMerchantBasicDataPath,
      'get'
    );
    if (params) {
      rb.path('merchantId', params.merchantId, {});
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<MerchantBasicDataViewModel>;
        })
      );
  }

  /**
   * Gets merchant.
   *
   * Finds and returns the saved resource with the specified id.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getMerchantBasicData$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getMerchantBasicData(
    params: {
      merchantId: string;
    },
    context?: HttpContext
  ): Observable<MerchantBasicDataViewModel> {
    return this.getMerchantBasicData$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<MerchantBasicDataViewModel>) =>
          r.body as MerchantBasicDataViewModel
      )
    );
  }

  /**
   * Path part for operation updateMerchantBasicData
   */
  static readonly UpdateMerchantBasicDataPath =
    '/merchants/{merchantId}/basic-data';

  /**
   * Updates merchant.
   *
   * Finds and returns the saved resource with the specified id.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateMerchantBasicData()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateMerchantBasicData$Response(
    params: {
      merchantId: string;
      body: MerchantBasicDataViewModel;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<void>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      MerchantsService.UpdateMerchantBasicDataPath,
      'put'
    );
    if (params) {
      rb.path('merchantId', params.merchantId, {});
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({
          responseType: 'text',
          accept: '*/*',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return (r as HttpResponse<any>).clone({
            body: undefined,
          }) as StrictHttpResponse<void>;
        })
      );
  }

  /**
   * Updates merchant.
   *
   * Finds and returns the saved resource with the specified id.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updateMerchantBasicData$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateMerchantBasicData(
    params: {
      merchantId: string;
      body: MerchantBasicDataViewModel;
    },
    context?: HttpContext
  ): Observable<void> {
    return this.updateMerchantBasicData$Response(params, context).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation updateRequestedMerchantTypeStatus
   */
  static readonly UpdateRequestedMerchantTypeStatusPath =
    '/merchants/admin/sites/merchant-types/status';

  /**
   * Updates the status of requested merchant types by admin.
   *
   * Updates the status of requested merchant types based on the provided list of request ids and a status.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateRequestedMerchantTypeStatus()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateRequestedMerchantTypeStatus$Response(
    params: {
      body: SiteMerchantTypeStatusUpdateRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<void>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      MerchantsService.UpdateRequestedMerchantTypeStatusPath,
      'put'
    );
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({
          responseType: 'text',
          accept: '*/*',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return (r as HttpResponse<any>).clone({
            body: undefined,
          }) as StrictHttpResponse<void>;
        })
      );
  }

  /**
   * Updates the status of requested merchant types by admin.
   *
   * Updates the status of requested merchant types based on the provided list of request ids and a status.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updateRequestedMerchantTypeStatus$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateRequestedMerchantTypeStatus(
    params: {
      body: SiteMerchantTypeStatusUpdateRequest;
    },
    context?: HttpContext
  ): Observable<void> {
    return this.updateRequestedMerchantTypeStatus$Response(
      params,
      context
    ).pipe(map((r: StrictHttpResponse<void>) => r.body as void));
  }

  /**
   * Path part for operation listMerchants
   */
  static readonly ListMerchantsPath = '/merchants';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `listMerchants()` instead.
   *
   * This method doesn't expect any request body.
   */
  listMerchants$Response(
    params?: {
      companyNameLike?: string;

      /**
       * Zero-based page index (0..N)
       */
      page?: number;

      /**
       * The size of the page to be returned
       */
      size?: number;

      /**
       * Sorting criteria in the format: property,(asc|desc). Default sort order is ascending. Multiple sort criteria are supported.
       */
      sort?: Array<string>;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<PagedModelMerchantResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      MerchantsService.ListMerchantsPath,
      'get'
    );
    if (params) {
      rb.query('companyNameLike', params.companyNameLike, {});
      rb.query('page', params.page, {});
      rb.query('size', params.size, {});
      rb.query('sort', params.sort, {});
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/hal+json',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<PagedModelMerchantResponse>;
        })
      );
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `listMerchants$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  listMerchants(
    params?: {
      companyNameLike?: string;

      /**
       * Zero-based page index (0..N)
       */
      page?: number;

      /**
       * The size of the page to be returned
       */
      size?: number;

      /**
       * Sorting criteria in the format: property,(asc|desc). Default sort order is ascending. Multiple sort criteria are supported.
       */
      sort?: Array<string>;
    },
    context?: HttpContext
  ): Observable<PagedModelMerchantResponse> {
    return this.listMerchants$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<PagedModelMerchantResponse>) =>
          r.body as PagedModelMerchantResponse
      )
    );
  }

  /**
   * Path part for operation registerMerchant
   */
  static readonly RegisterMerchantPath = '/merchants';

  /**
   * Starts registration of a new merchant.
   *
   * Creates a new merchant and returns the saved resource with its identifier.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `registerMerchant()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  registerMerchant$Response(
    params: {
      body: PostMerchantRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<MerchantViewModel>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      MerchantsService.RegisterMerchantPath,
      'post'
    );
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<MerchantViewModel>;
        })
      );
  }

  /**
   * Starts registration of a new merchant.
   *
   * Creates a new merchant and returns the saved resource with its identifier.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `registerMerchant$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  registerMerchant(
    params: {
      body: PostMerchantRequest;
    },
    context?: HttpContext
  ): Observable<MerchantViewModel> {
    return this.registerMerchant$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<MerchantViewModel>) =>
          r.body as MerchantViewModel
      )
    );
  }

  /**
   * Path part for operation listMerchantSites1
   */
  static readonly ListMerchantSites1Path = '/merchants/{merchantId}/sites';

  /**
   * List sites of merchant.
   *
   * Finds and returns the saved resource with the specified id.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `listMerchantSites1()` instead.
   *
   * This method doesn't expect any request body.
   */
  listMerchantSites1$Response(
    params: {
      merchantId: string;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<GetMerchantSitesResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      MerchantsService.ListMerchantSites1Path,
      'get'
    );
    if (params) {
      rb.path('merchantId', params.merchantId, {});
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<GetMerchantSitesResponse>;
        })
      );
  }

  /**
   * List sites of merchant.
   *
   * Finds and returns the saved resource with the specified id.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `listMerchantSites1$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  listMerchantSites1(
    params: {
      merchantId: string;
    },
    context?: HttpContext
  ): Observable<GetMerchantSitesResponse> {
    return this.listMerchantSites1$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<GetMerchantSitesResponse>) =>
          r.body as GetMerchantSitesResponse
      )
    );
  }

  /**
   * Path part for operation createMerchantSites
   */
  static readonly CreateMerchantSitesPath = '/merchants/{merchantId}/sites';

  /**
   * Create a new site of merchant.
   *
   * Finds and returns the saved resource with the specified id.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createMerchantSites()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createMerchantSites$Response(
    params: {
      merchantId: string;
      body: PostMerchantSiteRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<MerchantSiteViewModel>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      MerchantsService.CreateMerchantSitesPath,
      'post'
    );
    if (params) {
      rb.path('merchantId', params.merchantId, {});
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<MerchantSiteViewModel>;
        })
      );
  }

  /**
   * Create a new site of merchant.
   *
   * Finds and returns the saved resource with the specified id.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `createMerchantSites$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createMerchantSites(
    params: {
      merchantId: string;
      body: PostMerchantSiteRequest;
    },
    context?: HttpContext
  ): Observable<MerchantSiteViewModel> {
    return this.createMerchantSites$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<MerchantSiteViewModel>) =>
          r.body as MerchantSiteViewModel
      )
    );
  }

  /**
   * Path part for operation getAllMarketingDataByMerchant
   */
  static readonly GetAllMarketingDataByMerchantPath =
    '/merchants/{merchantId}/marketing-data';

  /**
   * Gets marketing data.
   *
   * Finds and returns the all marketing data by the specifed merchant.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getAllMarketingDataByMerchant()` instead.
   *
   * This method doesn't expect any request body.
   */
  getAllMarketingDataByMerchant$Response(
    params: {
      merchantId: string;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<ListMarketingDataViewModel>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      MerchantsService.GetAllMarketingDataByMerchantPath,
      'get'
    );
    if (params) {
      rb.path('merchantId', params.merchantId, {});
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<ListMarketingDataViewModel>;
        })
      );
  }

  /**
   * Gets marketing data.
   *
   * Finds and returns the all marketing data by the specifed merchant.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getAllMarketingDataByMerchant$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getAllMarketingDataByMerchant(
    params: {
      merchantId: string;
    },
    context?: HttpContext
  ): Observable<ListMarketingDataViewModel> {
    return this.getAllMarketingDataByMerchant$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<ListMarketingDataViewModel>) =>
          r.body as ListMarketingDataViewModel
      )
    );
  }

  /**
   * Path part for operation createMarketingDataToMerchant
   */
  static readonly CreateMarketingDataToMerchantPath =
    '/merchants/{merchantId}/marketing-data';

  /**
   * Creates marketing data.
   *
   * Create marketing data to the specified merchant
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createMarketingDataToMerchant()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createMarketingDataToMerchant$Response(
    params: {
      merchantId: string;
      body: MarketingDataDto;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<MarketingDataViewModel>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      MerchantsService.CreateMarketingDataToMerchantPath,
      'post'
    );
    if (params) {
      rb.path('merchantId', params.merchantId, {});
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<MarketingDataViewModel>;
        })
      );
  }

  /**
   * Creates marketing data.
   *
   * Create marketing data to the specified merchant
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `createMarketingDataToMerchant$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createMarketingDataToMerchant(
    params: {
      merchantId: string;
      body: MarketingDataDto;
    },
    context?: HttpContext
  ): Observable<MarketingDataViewModel> {
    return this.createMarketingDataToMerchant$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<MarketingDataViewModel>) =>
          r.body as MarketingDataViewModel
      )
    );
  }

  /**
   * Path part for operation calculateServedCustomerSegments
   */
  static readonly CalculateServedCustomerSegmentsPath =
    '/merchants/sites/calculate-servable-customer-segments';

  /**
   * Returns servable customer segments.
   *
   * Calculates servable customer segments with status set based on selected merchant types.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `calculateServedCustomerSegments()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  calculateServedCustomerSegments$Response(
    params: {
      body: Array<MerchantSiteMerchantTypeViewModel>;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<Array<ServedCustomerSegmentResponse>>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      MerchantsService.CalculateServedCustomerSegmentsPath,
      'post'
    );
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<Array<ServedCustomerSegmentResponse>>;
        })
      );
  }

  /**
   * Returns servable customer segments.
   *
   * Calculates servable customer segments with status set based on selected merchant types.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `calculateServedCustomerSegments$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  calculateServedCustomerSegments(
    params: {
      body: Array<MerchantSiteMerchantTypeViewModel>;
    },
    context?: HttpContext
  ): Observable<Array<ServedCustomerSegmentResponse>> {
    return this.calculateServedCustomerSegments$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<Array<ServedCustomerSegmentResponse>>) =>
          r.body as Array<ServedCustomerSegmentResponse>
      )
    );
  }

  /**
   * Path part for operation searchMerchant
   */
  static readonly SearchMerchantPath = '/merchants/search-by-segments';

  /**
   * Search merchants by filter and customer segments.
   *
   * Search merchants by the specified MerchantSearchDetailsFilter object and customer segments
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `searchMerchant()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  searchMerchant$Response(
    params: {
      /**
       * Zero-based page index (0..N)
       */
      page?: number;

      /**
       * The size of the page to be returned
       */
      size?: number;

      /**
       * Sorting criteria in the format: property,(asc|desc). Default sort order is ascending. Multiple sort criteria are supported.
       */
      sort?: Array<string>;
      body: MerchantSearchDetailsFilter;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<PageMerchantSearchResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      MerchantsService.SearchMerchantPath,
      'post'
    );
    if (params) {
      rb.query('page', params.page, {});
      rb.query('size', params.size, {});
      rb.query('sort', params.sort, {});
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<PageMerchantSearchResponse>;
        })
      );
  }

  /**
   * Search merchants by filter and customer segments.
   *
   * Search merchants by the specified MerchantSearchDetailsFilter object and customer segments
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `searchMerchant$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  searchMerchant(
    params: {
      /**
       * Zero-based page index (0..N)
       */
      page?: number;

      /**
       * The size of the page to be returned
       */
      size?: number;

      /**
       * Sorting criteria in the format: property,(asc|desc). Default sort order is ascending. Multiple sort criteria are supported.
       */
      sort?: Array<string>;
      body: MerchantSearchDetailsFilter;
    },
    context?: HttpContext
  ): Observable<PageMerchantSearchResponse> {
    return this.searchMerchant$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<PageMerchantSearchResponse>) =>
          r.body as PageMerchantSearchResponse
      )
    );
  }

  /**
   * Path part for operation getMerchantById
   */
  static readonly GetMerchantByIdPath = '/merchants/{merchantId}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getMerchantById()` instead.
   *
   * This method doesn't expect any request body.
   */
  getMerchantById$Response(
    params: {
      merchantId: string;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<MerchantResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      MerchantsService.GetMerchantByIdPath,
      'get'
    );
    if (params) {
      rb.path('merchantId', params.merchantId, {});
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/hal+json',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<MerchantResponse>;
        })
      );
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getMerchantById$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getMerchantById(
    params: {
      merchantId: string;
    },
    context?: HttpContext
  ): Observable<MerchantResponse> {
    return this.getMerchantById$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<MerchantResponse>) => r.body as MerchantResponse
      )
    );
  }

  /**
   * Path part for operation listTenders1
   */
  static readonly ListTenders1Path = '/merchants/{merchantId}/tenders';

  /**
   * Listing tenders of a merchant.
   *
   * Lists available tenders of specified merchant.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `listTenders1()` instead.
   *
   * This method doesn't expect any request body.
   */
  listTenders1$Response(
    params: {
      merchantId: string;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<PagedModelTenderResponseModel>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      MerchantsService.ListTenders1Path,
      'get'
    );
    if (params) {
      rb.path('merchantId', params.merchantId, {});
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<PagedModelTenderResponseModel>;
        })
      );
  }

  /**
   * Listing tenders of a merchant.
   *
   * Lists available tenders of specified merchant.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `listTenders1$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  listTenders1(
    params: {
      merchantId: string;
    },
    context?: HttpContext
  ): Observable<PagedModelTenderResponseModel> {
    return this.listTenders1$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<PagedModelTenderResponseModel>) =>
          r.body as PagedModelTenderResponseModel
      )
    );
  }

  /**
   * Path part for operation listTenders2
   */
  static readonly ListTenders2Path =
    '/merchants/{merchantId}/sites/{siteId}/tenders';

  /**
   * Listing tenders of a merchant for a site.
   *
   * Lists available tenders of specified merchant for the specified site.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `listTenders2()` instead.
   *
   * This method doesn't expect any request body.
   */
  listTenders2$Response(
    params: {
      merchantId: string;
      siteId: string;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<PagedModelTenderResponseModel>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      MerchantsService.ListTenders2Path,
      'get'
    );
    if (params) {
      rb.path('merchantId', params.merchantId, {});
      rb.path('siteId', params.siteId, {});
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<PagedModelTenderResponseModel>;
        })
      );
  }

  /**
   * Listing tenders of a merchant for a site.
   *
   * Lists available tenders of specified merchant for the specified site.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `listTenders2$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  listTenders2(
    params: {
      merchantId: string;
      siteId: string;
    },
    context?: HttpContext
  ): Observable<PagedModelTenderResponseModel> {
    return this.listTenders2$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<PagedModelTenderResponseModel>) =>
          r.body as PagedModelTenderResponseModel
      )
    );
  }

  /**
   * Path part for operation listAllDeliveryMethodsByMerchantIdAndSiteId
   */
  static readonly ListAllDeliveryMethodsByMerchantIdAndSiteIdPath =
    '/merchants/{merchantId}/sites/{siteId}/delivery/methods';

  /**
   * Create a new site of merchant.
   *
   * Finds and returns the saved resource with the specified id.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `listAllDeliveryMethodsByMerchantIdAndSiteId()` instead.
   *
   * This method doesn't expect any request body.
   */
  listAllDeliveryMethodsByMerchantIdAndSiteId$Response(
    params: {
      merchantId: string;
      siteId: string;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<PagedModelDeliveryMethodPageResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      MerchantsService.ListAllDeliveryMethodsByMerchantIdAndSiteIdPath,
      'get'
    );
    if (params) {
      rb.path('merchantId', params.merchantId, {});
      rb.path('siteId', params.siteId, {});
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/hal+json',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<PagedModelDeliveryMethodPageResponse>;
        })
      );
  }

  /**
   * Create a new site of merchant.
   *
   * Finds and returns the saved resource with the specified id.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `listAllDeliveryMethodsByMerchantIdAndSiteId$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  listAllDeliveryMethodsByMerchantIdAndSiteId(
    params: {
      merchantId: string;
      siteId: string;
    },
    context?: HttpContext
  ): Observable<PagedModelDeliveryMethodPageResponse> {
    return this.listAllDeliveryMethodsByMerchantIdAndSiteId$Response(
      params,
      context
    ).pipe(
      map(
        (r: StrictHttpResponse<PagedModelDeliveryMethodPageResponse>) =>
          r.body as PagedModelDeliveryMethodPageResponse
      )
    );
  }

  /**
   * Path part for operation getSiteSelectItemsOfMerchant
   */
  static readonly GetSiteSelectItemsOfMerchantPath =
    '/merchants/current/sites/select-items';

  /**
   * Gets site select items.
   *
   * Finds and returns site select items of logged in merchant.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getSiteSelectItemsOfMerchant()` instead.
   *
   * This method doesn't expect any request body.
   */
  getSiteSelectItemsOfMerchant$Response(
    params?: {},
    context?: HttpContext
  ): Observable<StrictHttpResponse<Array<SiteSelectItemDto>>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      MerchantsService.GetSiteSelectItemsOfMerchantPath,
      'get'
    );
    if (params) {
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<Array<SiteSelectItemDto>>;
        })
      );
  }

  /**
   * Gets site select items.
   *
   * Finds and returns site select items of logged in merchant.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getSiteSelectItemsOfMerchant$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getSiteSelectItemsOfMerchant(
    params?: {},
    context?: HttpContext
  ): Observable<Array<SiteSelectItemDto>> {
    return this.getSiteSelectItemsOfMerchant$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<Array<SiteSelectItemDto>>) =>
          r.body as Array<SiteSelectItemDto>
      )
    );
  }

  /**
   * Path part for operation getMerchantTypeRequestsForAdmin
   */
  static readonly GetMerchantTypeRequestsForAdminPath =
    '/merchants/admin/sites/merchant-types';

  /**
   * Gets merchant type requests.
   *
   * Finds and returns merchant type requests.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getMerchantTypeRequestsForAdmin()` instead.
   *
   * This method doesn't expect any request body.
   */
  getMerchantTypeRequestsForAdmin$Response(
    params?: {
      /**
       * Zero-based page index (0..N)
       */
      page?: number;

      /**
       * The size of the page to be returned
       */
      size?: number;

      /**
       * Sorting criteria in the format: property,(asc|desc). Default sort order is ascending. Multiple sort criteria are supported.
       */
      sort?: Array<string>;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<PageSiteMerchantTypesResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      MerchantsService.GetMerchantTypeRequestsForAdminPath,
      'get'
    );
    if (params) {
      rb.query('page', params.page, {});
      rb.query('size', params.size, {});
      rb.query('sort', params.sort, {});
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<PageSiteMerchantTypesResponse>;
        })
      );
  }

  /**
   * Gets merchant type requests.
   *
   * Finds and returns merchant type requests.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getMerchantTypeRequestsForAdmin$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getMerchantTypeRequestsForAdmin(
    params?: {
      /**
       * Zero-based page index (0..N)
       */
      page?: number;

      /**
       * The size of the page to be returned
       */
      size?: number;

      /**
       * Sorting criteria in the format: property,(asc|desc). Default sort order is ascending. Multiple sort criteria are supported.
       */
      sort?: Array<string>;
    },
    context?: HttpContext
  ): Observable<PageSiteMerchantTypesResponse> {
    return this.getMerchantTypeRequestsForAdmin$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<PageSiteMerchantTypesResponse>) =>
          r.body as PageSiteMerchantTypesResponse
      )
    );
  }
}
